// Anonymous Hub directory client (`/api/v1/platform/public/*`, Hub design 05).
//
// Deliberately NOT the shared `http()` instance: these reads must never carry a bearer token,
// trigger a refresh rotation or redirect to `/login`. A stale session on the public landing would
// otherwise turn a harmless directory read into a sign-in loop (Hub design 06 / FE-04).

import axios from 'axios';
import { config } from '../../core/config';
import type { ProfilePhoto } from '../hub/hubApi';

export interface PublicCategory {
  code: string;
  name: string;
  icon: string;
}

export interface PublicCategoryGroup extends PublicCategory {
  productCode: string;
  leaves: PublicCategory[];
}

export interface OrganizationCard {
  /** The organization profile (one business, for one product) this card shows. */
  id: string;
  /** The tenant that runs the business; favourites are kept per organization. */
  organizationId: string;
  slug: string;
  name: string;
  city: string | null;
  tagline: string | null;
  primaryCategory: PublicCategory | null;
  groupCode: string | null;
  groupName: string | null;
  productCode: string | null;
  isVerified: boolean;
  isFeatured: boolean;
  featuredRank: number | null;
  hasCustomUrl: boolean;
  hasOnlineBooking: boolean;
  hasShop: boolean;
  minPrice: number | null;
  currencyCode: string | null;
  openJobs: number;
  latitude: number | null;
  longitude: number | null;
  distanceKm: number | null;
  logoUrl: string | null;
  coverUrl: string | null;
  /** Media Server variants (absent for legacy photos); logoUrl/coverUrl are the 160 px logo and 480 px cover. */
  logo?: ProfilePhoto | null;
  cover?: ProfilePhoto | null;
}

export interface JobCard {
  id: string;
  title: string;
  roleCode: string;
  roleName: string | null;
  organizationSlug: string;
  organizationName: string;
  city: string;
  employmentType: string;
  experienceLevel: string;
  salaryMin: number | null;
  salaryMax: number | null;
  salaryCurrency: string | null;
  salaryPeriod: string | null;
  publishedAt: string | null;
}

export interface JobDetail {
  job: JobCard;
  description: string | null;
  requirements: string | null;
  benefits: string | null;
  addressLine: string | null;
  closesOn: string | null;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PublicHome {
  groups: PublicCategoryGroup[];
  featured: OrganizationCard[];
  nearYou: OrganizationCard[];
  latestJobs: JobCard[];
  counters: { organizations: number; openJobs: number; cities: number };
}

export interface Suggestions {
  organizations: { slug: string; name: string; city: string | null }[];
  categories: PublicCategory[];
  cities: string[];
}

export interface OrganizationPage {
  card: OrganizationCard;
  about: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  addressLine: string | null;
  postalCode: string | null;
  countryCode: string | null;
  workingHours: string | null;
  instagramUrl: string | null;
  facebookUrl: string | null;
  tikTokUrl: string | null;
  categories: PublicCategory[];
  openJobs: JobCard[];
  destinationKind: 'custom' | 'product' | 'platform';
  destinationUrl: string | null;
}

export interface Destination {
  productCode?: string | null;
  kind: 'custom' | 'product' | 'platform';
  url: string | null;
}

export interface DirectorySearch {
  q?: string;
  category?: string;
  city?: string;
  lat?: number;
  lng?: number;
  radiusKm?: number;
  bookableOnline?: boolean;
  hasShop?: boolean;
  hasJobs?: boolean;
  sort?: 'recommended' | 'distance' | 'name';
  page?: number;
  pageSize?: number;
}

/** A business's public post on the Hub (PlatformDB 066). */
export interface PostCard {
  id: string;
  content: string;
  createdAt: string;
  likeCount: number;
  commentCount: number;
  /** Photos in display order; Media Server variants. */
  media: ProfilePhoto[];
  business: {
    slug: string;
    name: string;
    city: string | null;
    logoUrl: string | null;
    groupCode: string | null;
    groupName: string | null;
    isVerified: boolean;
  };
}

export interface PostPage {
  items: PostCard[];
  nextCursor: string | null;
}

export interface PostComment {
  id: string;
  content: string;
  createdAt: string;
  authorName: string;
}

/** A product of a listed business (any product line), newest first — the landing "Products" rail. */
export interface ProductCard {
  id: string;
  productCode: string;
  itemRef: string;
  name: string;
  price: number;
  compareAtPrice: number | null;
  currency: string;
  imageUrl: string | null;
  createdAt: string;
  orderable: boolean;
  business: { slug: string; name: string; city: string | null; logoUrl: string | null; groupCode: string | null; groupName: string | null };
}

export type PostReportReason = 'Spam' | 'Inappropriate' | 'Misleading' | 'Harassment' | 'Other';

const client = axios.create({ baseURL: `${config.apiBaseUrl}/public`, timeout: 20_000 });

/** Drop empty/false values so URLs stay short and cacheable. */
function clean(params: object): Record<string, string | number | boolean> {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '' && value !== false));
}

export const publicApi = {
  // HUB-UI: job editor taxonomy comes from the anonymous, active job-role catalogue.
  async jobRoles(lang: string): Promise<{ code: string; name: string }[]> {
    const { data } = await client.get<{ code: string; name: string }[]>('/job-roles', { params: { lang } });
    return data;
  },
  async home(lang: string, point?: { lat: number; lng: number }): Promise<PublicHome> {
    const { data } = await client.get<PublicHome>('/home', { params: clean({ lang, ...point }) });
    return data;
  },
  async categories(lang: string): Promise<PublicCategoryGroup[]> {
    const { data } = await client.get<PublicCategoryGroup[]>('/categories', { params: { lang } });
    return data;
  },
  async organizations(lang: string, search: DirectorySearch): Promise<Paged<OrganizationCard>> {
    const { data } = await client.get<Paged<OrganizationCard>>('/organizations', { params: clean({ lang, ...search }) });
    return data;
  },
  async suggest(lang: string, q: string): Promise<Suggestions> {
    const { data } = await client.get<Suggestions>('/organizations/suggest', { params: { lang, q } });
    return data;
  },
  async organization(lang: string, slug: string): Promise<OrganizationPage> {
    const { data } = await client.get<OrganizationPage>(`/organizations/${encodeURIComponent(slug)}`, { params: { lang } });
    return data;
  },
  async destination(slug: string): Promise<Destination> {
    const { data } = await client.get<Destination>(`/organizations/${encodeURIComponent(slug)}/destination`);
    return data;
  },
  async jobs(lang: string, search: { q?: string; city?: string; role?: string; page?: number; pageSize?: number }): Promise<Paged<JobCard>> {
    const { data } = await client.get<Paged<JobCard>>('/jobs', { params: clean({ lang, ...search }) });
    return data;
  },
  async job(lang: string, jobId: string): Promise<JobDetail> {
    const { data } = await client.get<JobDetail>(`/jobs/${encodeURIComponent(jobId)}`, { params: { lang } });
    return data;
  },
  async posts(lang: string, search: { q?: string; city?: string; organization?: string; id?: string; cursor?: string | null; pageSize?: number } = {}): Promise<PostPage> {
    const { data } = await client.get<PostPage>('/posts', { params: clean({ lang, ...search }) });
    return data;
  },
  async products(lang: string, take = 16): Promise<ProductCard[]> {
    const { data } = await client.get<{ items: ProductCard[] }>('/products', { params: { lang, take } });
    return data.items;
  },
  async postComments(postId: string): Promise<PostComment[]> {
    const { data } = await client.get<PostComment[]>(`/posts/${encodeURIComponent(postId)}/comments`);
    return data;
  },
};

/** 404 from the directory means "not listed" — rendered as a not-found state, not an error. */
export function isNotFound(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}
